import { useId, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { ClearFiltersButton, SearchField, FilterField } from '../common/list-page.js'
import { FilterTh, describeColumnFilters } from '../common/column-filter-indicator.js'
import { TABLE, TABLE_HEAD_ROW, TABLE_ROW, TABLE_WRAP } from '../common/table.js'
import { DisclosureChevron, Button, Notice, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@speaking-coach/ui'
import { allTopics, topicHref } from './content-model.js'
import { CONTENT_HELP, ContentMetricLabel } from './content-help.js'
import { ContentRoute, ContentShell } from './content-shell.js'
import { ContentScopeFilters, ContentSelect, useContentScope } from './content-scope.js'
import type { BandStatus, ContentDashboard, ContentLevel } from './types.js'
import {
  filterVocabularyRows,
  readVocabularyFilters,
  VOCABULARY_VIEWS,
  vocabularyRows,
  vocabularySearchParams,
  vocabularySummary,
  type ExceptionState,
  type ReuseState,
  type VocabularyFilters,
  type VocabularyRow,
} from './vocabulary-model.js'

const CEFR_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']

export function ContentVocabularyPage(): JSX.Element {
  return (
    <ContentRoute>
      {(dashboard) => <VocabularyPage dashboard={dashboard} />}
    </ContentRoute>
  )
}

function VocabularyPage({
  dashboard,
}: {
  dashboard: ContentDashboard
}): JSX.Element {
  const { selectedLevel } = useContentScope(dashboard)
  return selectedLevel ? <VocabularyReport dashboard={dashboard} selectedLevel={selectedLevel} /> :
    <ContentShell title="Vocabulary" filters={<ContentScopeFilters dashboard={dashboard} required />}>
      <Notice variant="neutral">Select a Program and level to inspect vocabulary.</Notice>
    </ContentShell>
}

function VocabularyReport({ dashboard, selectedLevel }: { dashboard: ContentDashboard; selectedLevel: ContentLevel }): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = readVocabularyFilters(searchParams)
  const rows = useMemo(
    () => vocabularyRows(dashboard, selectedLevel),
    [dashboard, selectedLevel],
  )
  const filteredRows = filterVocabularyRows(rows, filters)
  const summary = vocabularySummary(rows)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const locations = new Map(
    allTopics(dashboard).map((location) => [location.topic.id, location]),
  )
  const grades = Array.from(
    new Set(
      rows.flatMap((row) =>
        row.occurrences.flatMap((item) => item.cefrLevel ?? []),
      ),
    ),
  ).sort((left, right) => CEFR_ORDER.indexOf(left) - CEFR_ORDER.indexOf(right))
  const themes = selectedLevel.themes.flatMap((theme) => ({
    value: theme.id,
    label: `T${theme.position} · ${theme.title.en}`,
  }))

  function commit(next: VocabularyFilters, level = selectedLevel.programLevelId): void {
    setSearchParams(vocabularySearchParams(level, next), { replace: true })
  }

  function update<K extends keyof VocabularyFilters>(
    key: K,
    value: VocabularyFilters[K],
  ): void {
    commit({ ...filters, [key]: value })
  }

  function toggle(lemma: string): void {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(lemma)) next.delete(lemma)
      else next.add(lemma)
      return next
    })
  }

  const returnContext = vocabularySearchParams(
    selectedLevel.programLevelId,
    filters,
  ).toString()

  return (
    <ContentShell title="Vocabulary"
      search={<SearchField id="vocabulary-search" label="Search English or Japanese" value={filters.q} onChange={event => update('q', event.target.value)} placeholder="Lemma, target, theme, or lesson" />}
      filterAction={<ClearFiltersButton hasFilters={hasFilters(filters)} onClear={() => commit({ ...filters, view: 'all', q: '', grade: '', band: '', exception: '', theme: '', reuse: '' })} />}
      filters={<>
        <ContentScopeFilters dashboard={dashboard} required />
        <ContentSelect label="View" value={filters.view === 'all' ? '' : filters.view} options={VOCABULARY_VIEWS.filter(view => view.value !== 'all').map(view => ({ value: view.value, label: view.label }))} emptyLabel="All vocabulary" onChange={value => update('view', (value || 'all') as VocabularyFilters['view'])} />
            <FilterSelect
              label="CEFR grade"
              value={filters.grade}
              onChange={(value) => update('grade', value)}
              options={grades.map((grade) => ({ value: grade, label: grade }))}
              help={CONTENT_HELP.cefrGrade}
            />
            <FilterSelect
              label="Band relationship"
              value={filters.band}
              onChange={(value) => update('band', value as BandStatus | '')}
              options={[
                { value: 'in_window', label: 'In band' },
                { value: 'above', label: 'Above band' },
                { value: 'below', label: 'Below band' },
                { value: 'ungraded', label: 'Ungraded' },
                { value: 'invalid', label: 'Invalid' },
              ]}
              help={CONTENT_HELP.bandRelationship}
            />
            <FilterSelect
              label="Exception state"
              value={filters.exception}
              onChange={(value) => update('exception', value as ExceptionState | '')}
              options={[
                { value: 'registered', label: 'Approved exception' },
                { value: 'unregistered', label: 'Missing approval' },
                { value: 'none', label: 'No exception' },
              ]}
              help={CONTENT_HELP.exceptionState}
            />
            <FilterSelect
              label="Reuse"
              value={filters.reuse}
              onChange={(value) => update('reuse', value as ReuseState | '')}
              options={[
                { value: 'single-use', label: 'Single use' },
                { value: 'within-level', label: 'Within a program level' },
                { value: 'cross-level', label: 'Across program levels' },
              ]}
              help={CONTENT_HELP.reuse}
            />
            <FilterSelect
              label="Theme"
              value={filters.theme}
              onChange={(value) => update('theme', value)}
              options={themes}
            />
      </>}
    >
      <section aria-label="Vocabulary report" className="space-y-4">
        <section aria-label={`${selectedLevel.level} vocabulary summary`}>
          {summary.invalidSlots > 0 ? (
            <Notice variant="warning" className="mb-3">
              {summary.invalidSlots} invalid vocabulary entries excluded from grading totals.{' '}
              <Link className="font-medium underline" to={metricHref(selectedLevel.programLevelId, { view: 'invalid' })}>Inspect invalid entries</Link> and correct the source content.
            </Notice>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric
              label="Target slots"
              value={summary.targetSlots}
              unit="slot occurrences"
              help={CONTENT_HELP.targetSlots}
            />
            <Metric
              label="Unique lemmas"
              value={summary.uniqueLemmas}
              unit="unique lemmas"
              help={CONTENT_HELP.uniqueLemmas}
            />
            <Metric
              label="In band"
              value={summary.inBandSlots}
              unit="slot occurrences"
              to={metricHref(selectedLevel.programLevelId, { band: 'in_window' })}
              help={CONTENT_HELP.inBand}
            />
            <Metric
              label="Below band"
              value={summary.belowBandSlots}
              unit="slot occurrences"
              to={metricHref(selectedLevel.programLevelId, { view: 'below' })}
              help={CONTENT_HELP.belowBand}
            />
            <Metric
              label="Above band"
              value={summary.aboveBandSlots}
              unit="slot occurrences"
              to={metricHref(selectedLevel.programLevelId, { view: 'above' })}
              attention={summary.aboveBandSlots > 0}
              help={CONTENT_HELP.aboveBand}
            />
            <Metric
              label="Ungraded"
              value={summary.ungradedSlots}
              unit="slot occurrences"
              to={metricHref(selectedLevel.programLevelId, { view: 'ungraded' })}
              help={CONTENT_HELP.ungraded}
            />
            <Metric
              label="Missing approval"
              value={summary.unregisteredSlots}
              unit="slot occurrences"
              to={metricHref(selectedLevel.programLevelId, { view: 'unregistered' })}
              attention={summary.unregisteredSlots > 0}
              help={CONTENT_HELP.unregistered}
            />
            <Metric
              label="Repeated"
              value={summary.repeatedLemmas}
              unit="unique lemmas"
              to={metricHref(selectedLevel.programLevelId, { view: 'repeated' })}
              help={CONTENT_HELP.repeated}
            />
          </div>
        </section>

        <p className="text-body text-text-secondary" aria-live="polite">{filteredRows.length} of {rows.length} vocabulary rows · {selectedLevel.programName.en} · {selectedLevel.level}</p>

        {filteredRows.length === 0 ? (
          <Notice variant="neutral">No lemmas match these filters.</Notice>
        ) : (
          <VocabularyTable
            filters={filters}
            rows={filteredRows}
            expanded={expanded}
            toggle={toggle}
            topicHrefFor={(topicId) => {
              const location = locations.get(topicId)
              return location
                ? topicHref(
                    location,
                    returnContext,
                    'vocabulary',
                    selectedLevel.programLevelId,
                  )
                : undefined
            }}
          />
        )}
      </section>
    </ContentShell>
  )
}

function hrefFor(
  level: string,
  filters: VocabularyFilters,
  updates: Partial<VocabularyFilters>,
): string {
  return `/admin/content/vocabulary?${vocabularySearchParams(level, {
    ...filters,
    ...updates,
  }).toString()}`
}

const NO_FILTERS: VocabularyFilters = {
  view: 'all',
  q: '',
  grade: '',
  band: '',
  exception: '',
  theme: '',
  reuse: '',
}

/**
 * Summary cards count the whole level, so they drill down to exactly that
 * population. Composing them with the active filters would let a card
 * advertising a non-zero count navigate to an empty table.
 */
function metricHref(
  level: string,
  updates: Partial<VocabularyFilters>,
): string {
  return hrefFor(level, NO_FILTERS, updates)
}

function hasFilters(filters: VocabularyFilters): boolean {
  return (
    filters.view !== 'all' ||
    Boolean(
      filters.q ||
        filters.grade ||
        filters.band ||
        filters.exception ||
        filters.theme ||
        filters.reuse,
    )
  )
}

function Metric({
  label,
  value,
  unit,
  to,
  attention = false,
  help,
}: {
  label: string
  value: number
  unit: string
  to?: string
  attention?: boolean
  help?: string
}): JSX.Element {
  const valueContent = (
    <>
      <p className={`mt-1 text-product-title font-semibold ${attention ? 'text-danger-text' : ''}`}>
        {value}
      </p>
      <p className="mt-1 text-caption text-text-secondary">{unit}</p>
    </>
  )
  const classes =
    'rounded-token-card border border-border-default bg-surface-elev p-4'
  return to ? (
    <div className={`${classes} relative transition-colors hover:border-accent`}>
      <Link
        to={to}
        aria-label={`${label} ${value} ${unit}`}
        className="absolute inset-0 rounded-token-card focus-visible:focus-ring"
      />
      <div className="pointer-events-none relative">
        <ContentMetricLabel label={label} help={help} uppercase />
        {valueContent}
      </div>
    </div>
  ) : (
    <div className={classes}>
      <ContentMetricLabel label={label} help={help} uppercase />
      {valueContent}
    </div>
  )
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  help,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (value: string) => void
  help?: string
}): JSX.Element {
  const selectable =
    value && !options.some((option) => option.value === value)
      ? [...options, { value, label: `${value} (not in this level)` }]
      : options
  return (
    <FilterField label={label} active={value !== ''} help={help}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="">All</SelectItem>
          {selectable.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterField>
  )
}

function VocabularyTable({
  filters,
  rows,
  expanded,
  toggle,
  topicHrefFor,
}: {
  filters: VocabularyFilters
  rows: VocabularyRow[]
  expanded: Set<string>
  toggle: (lemma: string) => void
  topicHrefFor: (topicId: string) => string | undefined
}): JSX.Element {
  const columnFilters: Record<string, string> = {
    Lemma: filters.q ? `Search: “${filters.q}”` : '',
    'CEFR grade': describeColumnFilters(filters.grade && `Grade: ${filters.grade}`, filters.view === 'ungraded' && 'Ungraded only'),
    'Level relationship': describeColumnFilters(filters.band && `Band: ${filters.band}`, ['above', 'below'].includes(filters.view) && `View: ${filters.view}`),
    Occurrences: describeColumnFilters(filters.theme && 'Selected theme', filters.q && `Search: “${filters.q}”`),
    'Exception state': describeColumnFilters(filters.exception && `Exception: ${filters.exception}`, ['registered', 'unregistered'].includes(filters.view) && `View: ${filters.view}`),
    Reuse: describeColumnFilters(filters.reuse && reuseLabel(filters.reuse), filters.view === 'repeated' && 'Repeated lemmas only'),
  }
  return (
    <div className={TABLE_WRAP} data-testid="content-vocabulary-table">
      <table className={`${TABLE} min-w-[58rem]`}>
        <thead>
          <tr className={TABLE_HEAD_ROW}>
            <th className="w-12 px-3 py-2"><span className="sr-only">Details</span></th>
            {['Lemma', 'CEFR grade', 'Level relationship', 'Occurrences', 'Exception state', 'Reuse'].map((label) => (
              <FilterTh key={label} label={label} filterDescription={columnFilters[label]} className="px-3 py-2 text-[10.5px] leading-4 font-medium uppercase tracking-wide text-text-secondary" />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const open = expanded.has(row.key ?? row.lemma)
            const first = row.occurrences[0]!
            return (
              <VocabularyTableRow
                key={row.key ?? row.lemma}
                row={row}
                open={open}
                first={first}
                toggle={toggle}
                topicHrefFor={topicHrefFor}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function VocabularyTableRow({
  row,
  open,
  first,
  toggle,
  topicHrefFor,
}: {
  row: VocabularyRow
  open: boolean
  first: VocabularyRow['occurrences'][number]
  toggle: (lemma: string) => void
  topicHrefFor: (topicId: string) => string | undefined
}): JSX.Element {
  const occurrencesId = useId()
  const values = <T,>(select: (item: typeof first) => T): T[] =>
    Array.from(new Set(row.occurrences.map(select)))
  const otherPrograms = Array.from(
    new Set(
      row.allOccurrences
        .filter((item) => item.programLevelContentKey !== first.programLevelContentKey)
        .map((item) => item.programLevelContentKey),
    ),
  )
  return (
    <>
      <tr className={`${TABLE_ROW} align-top`}>
        <td className="px-2 py-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={`${open ? 'Hide' : 'Show'} occurrences for ${row.lemma}`}
            aria-expanded={open}
            aria-controls={occurrencesId}
            onClick={() => toggle(row.key ?? row.lemma)}
          >
            <DisclosureChevron expanded={open} direction="right" />
          </Button>
        </td>
        <td className="px-3 py-3"><span className="font-semibold">{row.lemma}</span><span className="block text-table-secondary text-text-secondary">{first.en} · {first.ja}</span></td>
        <td className="px-3 py-3">{values((item) => item.bandStatus === 'invalid' ? 'Not analyzed' : item.cefrLevel ?? 'Ungraded').join(' · ')}</td>
        <td className="px-3 py-3">{values((item) => bandLabel(item.bandStatus)).join(' · ')}</td>
        <td className="px-3 py-3 tabular-nums">{row.occurrences.length} slot{row.occurrences.length === 1 ? '' : 's'}</td>
        <td className="px-3 py-3">{values((item) => exceptionLabel(item.exceptionState)).join(' · ')}</td>
        <td className="px-3 py-3">{reuseLabel(row.reuse)}</td>
      </tr>
      {open ? (
        <tr id={occurrencesId} className="border-b border-border-default bg-surface-app">
          <td colSpan={7} className="px-4 py-4">
            <p className="text-table-secondary text-text-secondary">
              Other programs: {otherPrograms.length ? otherPrograms.join(', ') : 'None'}
            </p>
            <ul className="mt-3 grid gap-3 lg:grid-cols-2">
              {row.occurrences.map((item, index) => {
                const href = topicHrefFor(item.topic.id)
                return (
                  <li key={`${item.topic.id}:${index}`} className="rounded-token-card border border-border-default bg-surface-elev p-3 text-table">
                    <p className="font-medium">{item.en} · {item.ja}</p>
                    <p className="mt-1 text-table-secondary text-text-secondary">{item.programLevelContentKey} · T{item.themePosition} {item.themeTitle} · #{item.topic.position}</p>
                    <p className="mt-2">{href ? <Link to={href} className="font-medium text-text-primary hover:underline">{item.topic.title}</Link> : item.topic.title}</p>
                    {item.invalidReason ? <p className="mt-2 text-table-secondary text-danger-text">{item.invalidReason}. Correct the source content.</p> : null}
                    <p className="mt-2 text-table-secondary text-text-secondary">Grade {item.bandStatus === 'invalid' ? 'not analyzed' : item.cefrLevel ?? 'ungraded'} · {bandLabel(item.bandStatus)} · {exceptionLabel(item.exceptionState)}</p>
                    {item.waiverJustification ? <p className="mt-1 text-table-secondary">Exception: {item.waiverJustification}</p> : null}
                  </li>
                )
              })}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  )
}

function bandLabel(status: BandStatus): string {
  if (status === 'in_window') return 'In band'
  if (status === 'above') return 'Above band'
  if (status === 'below') return 'Below band'
  if (status === 'invalid') return 'Invalid'
  return 'Ungraded'
}

function exceptionLabel(state: ExceptionState): string {
  if (state === 'invalid') return 'Not analyzed'
  if (state === 'registered') return 'Approved exception'
  if (state === 'unregistered') return 'Missing approval'
  return 'No exception'
}

function reuseLabel(state: ReuseState): string {
  if (state === 'cross-level') return 'Across program levels'
  if (state === 'within-level') return 'Within a program level'
  return 'Single use'
}
